import React, { useState } from 'react';

const digitsOnly = (v: string) => (v || '').replace(/\D/g, '').slice(0, 10);

const formatPhone = (d: string) => {
  if (d.length > 6) return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
  if (d.length > 3) return `(${d.slice(0, 3)}) ${d.slice(3)}`;
  if (d.length > 0) return `(${d}`;
  return '';
};

const SmsConsentPreview = () => {
  const [phone, setPhone] = useState('');
  const [checked, setChecked] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const canSubmit = digitsOnly(phone).length === 10 && checked;

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPhone(formatPhone(digitsOnly(e.target.value)));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitted(true);
  };

  return (
    <div className='sms-optin-page'>
      <style>{`
        .sms-optin-page {
          --ink: #0e1a2b;
          --ink-soft: #3b4a5e;
          --ink-muted: #6b7a8f;
          --orange: #e26a2c;
          --green: #1f6b3a;
          --green-soft: #e8f1ea;
          --rule: #e6e8ea;
          --bg: #f6f7f4;
          font-family: 'Inter', -apple-system, BlinkMacSystemFont, system-ui, sans-serif;
          color: var(--ink);
          background: var(--bg);
          line-height: 1.55;
          -webkit-font-smoothing: antialiased;
          min-height: 100%;
          display: flex;
          flex-direction: column;
        }
        .sms-optin-page * { box-sizing: border-box; }
        .sms-optin-page header {
          background: #fff;
          border-bottom: 1px solid var(--rule);
          padding: 20px 24px;
        }
        .sms-optin-page .brand {
          max-width: 960px;
          margin: 0 auto;
          display: flex;
          align-items: center;
          justify-content: space-between;
        }
        .sms-optin-page .wordmark { font-family: Georgia, serif; font-weight: 700; font-size: 22px; letter-spacing: -0.01em; }
        .sms-optin-page .wordmark .dot { color: var(--orange); }
        .sms-optin-page .wordmark .ai { color: var(--green); }
        .sms-optin-page .brand-tag { font-size: 12px; color: var(--ink-muted); letter-spacing: 0.04em; }
        .sms-optin-page main {
          flex: 1;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 48px 20px;
        }
        .sms-optin-page .card {
          background: #fff;
          border: 1px solid var(--rule);
          border-radius: 14px;
          box-shadow: 0 20px 60px rgba(14,26,43,0.08);
          width: 100%;
          max-width: 460px;
          padding: 36px 34px 30px;
        }
        .sms-optin-page .eyebrow {
          font-size: 12px;
          letter-spacing: 0.18em;
          text-transform: uppercase;
          color: var(--green);
          font-weight: 700;
          margin: 0 0 12px;
        }
        .sms-optin-page h1 {
          font-family: Georgia, serif;
          font-weight: 700;
          font-size: 28px;
          line-height: 1.12;
          letter-spacing: -0.015em;
          margin: 0 0 12px;
          color: var(--ink);
        }
        .sms-optin-page .lead {
          font-size: 15px;
          color: var(--ink-soft);
          margin: 0 0 24px;
        }
        .sms-optin-page label.field-label {
          display: block;
          font-size: 12px;
          letter-spacing: 0.14em;
          text-transform: uppercase;
          color: var(--ink-muted);
          font-weight: 600;
          margin-bottom: 6px;
        }
        .sms-optin-page input[type="tel"] {
          width: 100%;
          padding: 13px 14px;
          border: 1px solid var(--rule);
          border-radius: 8px;
          font-family: inherit;
          font-size: 16px;
          color: var(--ink);
          background: #fff;
          outline: none;
          transition: border-color .15s, box-shadow .15s;
        }
        .sms-optin-page input[type="tel"]:focus {
          border-color: var(--green);
          box-shadow: 0 0 0 3px var(--green-soft);
        }
        .sms-optin-page .consent {
          display: flex;
          gap: 10px;
          align-items: flex-start;
          margin: 18px 0 22px;
          font-size: 12.5px;
          line-height: 1.5;
          color: var(--ink-soft);
          cursor: pointer;
        }
        .sms-optin-page .consent input[type="checkbox"] {
          margin-top: 2px;
          width: 18px;
          height: 18px;
          flex-shrink: 0;
          accent-color: var(--green);
          cursor: pointer;
        }
        .sms-optin-page .consent a { color: var(--green); text-decoration: underline; }
        .sms-optin-page button.submit {
          width: 100%;
          padding: 14px 20px;
          background: var(--ink);
          color: #fff;
          border: none;
          border-radius: 8px;
          font-family: Georgia, serif;
          font-weight: 700;
          font-size: 16px;
          cursor: pointer;
          transition: background .15s, opacity .15s;
        }
        .sms-optin-page button.submit:hover:not(:disabled) { background: var(--orange); }
        .sms-optin-page button.submit:disabled { opacity: .45; cursor: not-allowed; }
        .sms-optin-page .fineprint {
          font-size: 11.5px;
          color: var(--ink-muted);
          margin: 14px 0 0;
          text-align: center;
        }
        .sms-optin-page .success { text-align: center; padding: 8px 0; }
        .sms-optin-page .success .check {
          width: 56px; height: 56px; border-radius: 50%;
          background: var(--green-soft); color: var(--green);
          display: inline-flex; align-items: center; justify-content: center;
          font-size: 28px; margin-bottom: 14px;
        }
        .sms-optin-page .success h2 { font-family: Georgia, serif; font-weight: 700; font-size: 22px; margin: 0 0 8px; }
        .sms-optin-page .success p { font-size: 14px; color: var(--ink-soft); margin: 0; }
        .sms-optin-page footer {
          border-top: 1px solid var(--rule);
          background: #fff;
          padding: 20px 24px;
          text-align: center;
          font-size: 12px;
          color: var(--ink-muted);
        }
        .sms-optin-page footer a { color: var(--ink-muted); text-decoration: none; border-bottom: 1px solid var(--rule); }
        .sms-optin-page footer .sep { margin: 0 10px; opacity: .5; }
      `}</style>

      <header>
        <div className='brand'>
          <div className='wordmark'>Meeth<span className='dot'>.</span><span className='ai'>ai</span></div>
          <div className='brand-tag'>Order updates by text</div>
        </div>
      </header>

      <main>
        <div className='card'>
          {!submitted && (
            <form onSubmit={handleSubmit}>
              <p className='eyebrow'>Order updates</p>
              <h1>Get texts about your order</h1>
              <p className='lead'>
                When you order from a restaurant on Meeth, enter your mobile number to
                get text updates about that order — confirmation, a secure payment link
                for phone orders, and a note when it&rsquo;s ready for pickup.
              </p>

              <label className='field-label' htmlFor='phone'>Mobile number</label>
              <input
                id='phone'
                name='phone'
                type='tel'
                autoComplete='tel'
                placeholder='(415) 555-0123'
                value={phone}
                onChange={handlePhoneChange}
              />

              <label className='consent' htmlFor='sms-consent'>
                <input
                  type='checkbox'
                  id='sms-consent'
                  name='sms_consent'
                  value='true'
                  checked={checked}
                  onChange={(e) => setChecked(e.target.checked)}
                />
                <span>
                  I agree to receive SMS order and delivery updates from Meeth at the
                  phone number provided. Message frequency varies. Message and data
                  rates may apply. Reply STOP to unsubscribe, HELP for help. View our{' '}
                  <a href='https://soopra.ai/meeth/terms#privacy' target='_blank' rel='noopener noreferrer'>
                    Privacy Policy
                  </a>{' '}
                  and{' '}
                  <a href='https://soopra.ai/meeth/terms#terms' target='_blank' rel='noopener noreferrer'>
                    Terms &amp; Conditions
                  </a>.
                </span>
              </label>

              <button type='submit' className='submit' disabled={!canSubmit}>
                Sign up for order texts
              </button>
              <p className='fineprint'>Consent is not a condition of any purchase.</p>
            </form>
          )}

          {submitted && (
            <div className='success'>
              <div className='check'>&#10003;</div>
              <h2>You&rsquo;re signed up</h2>
              <p>We&rsquo;ll text you about your Meeth orders. Reply STOP anytime to opt out.</p>
            </div>
          )}
        </div>
      </main>

      <footer>
        Meeth is a product of Soopra&nbsp;Inc.
        <span className='sep'>·</span>
        <a href='https://soopra.ai/meeth/terms#privacy' target='_blank' rel='noopener noreferrer'>Privacy Policy</a>
        <span className='sep'>·</span>
        <a href='https://soopra.ai/meeth/terms#terms' target='_blank' rel='noopener noreferrer'>Terms &amp; Conditions</a>
      </footer>
    </div>
  );
};

export default SmsConsentPreview;
